import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * FreeThrowChatPanel — SSE streaming chat for FT biomechanical analysis
 *
 * Mirrors GeminiChatPanel (CS3sBiomechDashboard.jsx:1332) adapted for
 * the free throw analysis context. Streams from POST /api/free-throw/stream
 * which uses Claude Opus on Vertex AI with FT synthesis + attempts injected.
 *
 * Security: All assistant markdown is sanitized via DOMPurify before rendering.
 */

marked.setOptions({ breaks: true, gfm: true });

const API_BASE = process.env.REACT_APP_API_URL || '';

const cardStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: 20,
};

const badgeStyle = (color) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  background: `${color}22`,
  color: color,
  border: `1px solid ${color}44`,
});

export default function FreeThrowChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Abort in-flight SSE on unmount to prevent memory leaks
  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages, streamingText]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    const userMsg = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setStreamingText('');

    try {
      // Abort any previous in-flight request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const response = await fetch(`${API_BASE}/api/free-throw/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-10),
        }),
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'text') {
              fullText += event.content;
              setStreamingText(fullText);
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue; // skip malformed SSE lines
            throw e; // re-throw server error events
          }
        }
      }

      setMessages(prev => [...prev, { role: 'assistant', content: fullText }]);
      setStreamingText('');
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
      setStreamingText('');
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedQuestions = [
    'What biomechanical tells correlate with missed_short free throws?',
    "Compare Stephon Castle's speed profile on made vs. missed shots",
    'Which inter-phase transition should we focus on in practice?',
    'How does toe duration relate to FT accuracy?',
    "What's the optimal knee angle based on the data?",
    'What drills would address the most common miss type?',
  ];

  // DOMPurify sanitizes all HTML before rendering to prevent XSS
  const renderMarkdown = (text) => {
    try { return DOMPurify.sanitize(marked.parse(text)); } catch { return DOMPurify.sanitize(text); }
  };

  const chatBubbleStyle = (role) => ({
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.7,
    wordBreak: 'break-word',
    ...(role === 'user'
      ? { background: 'rgba(34, 197, 94, 0.15)', color: '#bbf7d0', alignSelf: 'flex-end', borderBottomRightRadius: 4, whiteSpace: 'pre-wrap' }
      : { background: 'rgba(255,255,255,0.05)', color: '#d1d5db', alignSelf: 'flex-start', borderBottomLeftRadius: 4 }
    ),
  });

  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', height: 520 }}>
      <style>{`
        .ft-chat-md h1, .ft-chat-md h2, .ft-chat-md h3, .ft-chat-md h4 {
          margin: 8px 0 4px 0; color: #e2e8f0; font-size: 14px;
        }
        .ft-chat-md h1 { font-size: 16px; }
        .ft-chat-md h2 { font-size: 15px; }
        .ft-chat-md h3 { font-size: 14px; }
        .ft-chat-md p { margin: 4px 0; }
        .ft-chat-md ul, .ft-chat-md ol { margin: 4px 0; padding-left: 20px; }
        .ft-chat-md li { margin: 2px 0; }
        .ft-chat-md strong { color: #bbf7d0; }
        .ft-chat-md code { background: rgba(34,197,94,0.15); padding: 1px 4px; border-radius: 3px; font-size: 12px; }
        .ft-chat-md pre { background: rgba(0,0,0,0.3); padding: 8px; border-radius: 6px; overflow-x: auto; margin: 6px 0; }
        .ft-chat-md pre code { background: none; padding: 0; }
        .ft-chat-md table { border-collapse: collapse; width: 100%; margin: 6px 0; font-size: 12px; }
        .ft-chat-md th, .ft-chat-md td { border: 1px solid rgba(255,255,255,0.12); padding: 4px 8px; text-align: left; }
        .ft-chat-md th { background: rgba(34,197,94,0.15); color: #bbf7d0; font-weight: 600; }
        .ft-chat-md hr { border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 8px 0; }
        .ft-chat-md blockquote { border-left: 3px solid #22c55e; margin: 6px 0; padding: 4px 12px; color: #9ca3af; }
        .ft-chat-md a { color: #4ade80; text-decoration: none; }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Ask SLAM AI — Free Throw Analysis</h3>
        <span style={badgeStyle('#22c55e')}>Claude Opus 4.6</span>
      </div>

      {/* Messages area */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 4 }}>
        {messages.length === 0 && !streamingText && (
          <div style={{ textAlign: 'center', color: '#888', marginTop: 40 }}>
            <p style={{ fontSize: 14, marginBottom: 16 }}>Ask about the VW-FTS free throw biomechanical analysis.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(q); }}
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)',
                    color: '#86efac', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                    textAlign: 'left', maxWidth: 280,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={chatBubbleStyle(msg.role)}>
            {msg.role === 'user' ? msg.content : (
              <div className="ft-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} /> /* DOMPurify-sanitized */
            )}
          </div>
        ))}
        {streamingText && (
          <div style={chatBubbleStyle('assistant')}>
            <div className="ft-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText) }} /> /* DOMPurify-sanitized */
            <span style={{ animation: 'blink 1s infinite', color: '#22c55e' }}>|</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask about free throw mechanics, tells, coaching drills..."
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 10,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', fontSize: 13, outline: 'none',
          }}
        />
        <button
          onClick={sendMessage}
          disabled={isLoading || !input.trim()}
          style={{
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: isLoading ? '#333' : '#22c55e', color: '#fff',
            fontSize: 13, fontWeight: 600, cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? '...' : 'Send'}
        </button>
      </div>
    </div>
  );
}
